/**
 * Model řádků destinací karty člověka (editor `DestinationTeamEditPage`, seznam
 * `DestinationTeamsPage`). Čistý modul bez Reactu a bez `api`: testy běží v `node`.
 *
 * Role a pořadí platí U KAŽDÉ destinace zvlášť (vize podnety-tymu-2026-10, F6.1); první řádek
 * je primární destinace a jeho role a pořadí jsou role a pořadí karty (`sectionType`,
 * `sortOrder`), které čtou starší čtenáři karty. Pole karty se proto u karty s destinacemi
 * nevyplňují zvlášť, plynou z prvního řádku.
 *
 * Žádný literál typu sekce (guard `packages/shared/src/teamSectionTypesGuard.test.ts`):
 * kanonické pořadí rolí je pořadí `TEAM_SECTION_TYPES`, popisky dodává stránka.
 */
import { compareCzech } from "@ckgo2/shared";
import type { DestinationTeamCreateInput, DestinationTeamLinkInput } from "@ckgo2/shared/schemas";
import {
  TEAM_SECTION_TYPES,
  type AdminTeamDestinationLink,
  type AdminTeamListItem,
  type DestinationTeamSection,
  type TeamSectionType,
} from "@ckgo2/shared/types";

/**
 * Řádek destinace ve formuláři editoru. Na rozdíl od vstupu API (`role`/`sortOrder` tam smějí
 * chybět kvůli starším klientům) nese řádek formuláře roli i pořadí vždy: plní ho
 * `linksForIds` a detail API (`AdminTeamDestinationLink`), jiný zdroj řádků není.
 */
export type TeamFormLink = DestinationTeamLinkInput & { role: TeamSectionType; sortOrder: number };

/** Hodnoty formuláře editoru karty: vstup API s úplnými řádky destinací (první = primární). */
export type TeamFormValues = Omit<DestinationTeamCreateInput, "destinationLinks"> & {
  destinationLinks: TeamFormLink[];
};

/** `GET /api/admin/destination-teams/:id`: řádek karty a vazby (první = primární). */
export type AdminTeamDetail = DestinationTeamSection & {
  destinationLinks: AdminTeamDestinationLink[];
};

/** Jiná karta u téže destinace (nápověda „U destinace dále: …"). */
export interface TeamOther {
  id: string;
  name: string;
  /** Role a pořadí z vazby na TU destinaci, ne z karty. */
  role: TeamSectionType;
  sortOrder: number;
  isActive: boolean;
}

interface CardFields {
  sectionType: TeamSectionType;
  sortOrder: number;
}

const roleRank = (role: TeamSectionType) => TEAM_SECTION_TYPES.indexOf(role);

/**
 * Hodnota výběru (id v pořadí výběru) → řádky formuláře. Ponechané id drží svůj dosavadní
 * řádek (role, pořadí, telefon), odebrané mizí. Nový řádek na prvním místě dostane roli
 * a pořadí karty (prodejce bez destinací zůstane po přidání první destinace prodejcem), každý
 * další roli prvního řádku a pořadí 0, aby nový člověk u destinace nepředběhl dosavadní
 * nastavené pořadí. Řádek nese roli a pořadí hned, protože `useEditPage` po uložení resetuje
 * formulář hodnotami formuláře, ne tělem uložení.
 */
export function linksForIds(
  links: readonly TeamFormLink[],
  ids: readonly string[],
  card: CardFields,
): TeamFormLink[] {
  const byId = new Map(links.map((l) => [l.destinationId, l]));
  const out: TeamFormLink[] = [];
  for (const id of ids) {
    const kept = byId.get(id);
    if (kept) {
      out.push(kept);
      continue;
    }
    const first = out[0];
    out.push(
      first
        ? { destinationId: id, role: first.role, sortOrder: 0 }
        : { destinationId: id, role: card.sectionType, sortOrder: card.sortOrder },
    );
  }
  return out;
}

/** Role a pořadí karty plynoucí z řádků: první řádek. `null` = karta bez destinací drží svá pole. */
export function cardFieldsFromLinks(links: readonly TeamFormLink[]): CardFields | null {
  const first = links[0];
  return first ? { sectionType: first.role, sortOrder: first.sortOrder } : null;
}

/**
 * Tělo uložení (`transformOnSave`): `sectionType`/`sortOrder` karty = první řádek (konzistence
 * s živou synchronizací v editoru), `guideId` `""` → `null` (schéma `guideId` nemá
 * `blankToUndefined`, prázdný řetězec by spadl na 400).
 */
export function teamBodyForSave(data: TeamFormValues): DestinationTeamCreateInput {
  const out: TeamFormValues = { ...data };
  if (out.guideId === "") out.guideId = null;
  const card = cardFieldsFromLinks(data.destinationLinks);
  if (card) {
    out.sectionType = card.sectionType;
    out.sortOrder = card.sortOrder;
  }
  return out;
}

/**
 * Ostatní karty u destinace pro nápovědu: karty seznamu s vazbou na `destinationId` (primární
 * i další), bez karty `selfId`; role a pořadí z TÉ vazby. Řazení jako web: kanonické pořadí
 * role, pořadí vzestupně, česká abeceda jména. Neaktivní karty jsou ve výčtu s příznakem.
 */
export function othersAtDestination(
  teams: readonly AdminTeamListItem[],
  destinationId: string,
  selfId: string | null,
): TeamOther[] {
  const out: TeamOther[] = [];
  for (const team of teams) {
    if (team.id === selfId) continue;
    const at = team.destinationLinks.find((l) => l.destinationId === destinationId);
    if (!at) continue;
    out.push({
      id: team.id,
      name: team.name,
      role: at.role,
      sortOrder: at.sortOrder,
      isActive: team.isActive,
    });
  }
  return out.sort(
    (a, b) =>
      roleRank(a.role) - roleRank(b.role) ||
      a.sortOrder - b.sortOrder ||
      compareCzech(a.name, b.name),
  );
}

/**
 * Role karty u jejích destinací, bez duplicit, v kanonickém pořadí. Čte jen vazby: karta bez
 * destinací má `[]` bez ohledu na `sectionType` (na stránkách destinací se neukáže).
 */
export function teamRoles(team: Pick<AdminTeamListItem, "destinationLinks">): TeamSectionType[] {
  const roles = new Set(team.destinationLinks.map((l) => l.role));
  return TEAM_SECTION_TYPES.filter((role) => roles.has(role));
}

/** Má karta roli `role` u kterékoli své destinace? (filtr seznamu karet) */
export function teamHasRole(
  team: Pick<AdminTeamListItem, "destinationLinks">,
  role: TeamSectionType,
): boolean {
  return team.destinationLinks.some((l) => l.role === role);
}

/** Pořadí z textu číselného pole: celé číslo ≥ 0; prázdné nebo nečíselné = 0 (`NaN` by
 *  neprošlo `z.number().int()`). */
export function parseSortOrder(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.trunc(n));
}
